/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-05 14:24:28                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-24 15:23:21                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Sections
  * Services
  * Styles
  * Planning: display the planning view
    * useEffect: Get times from the API
*/

/* Imports */
import { useCallback, useEffect, useState } from "react";
import { endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth as startOfCalendarMonth, startOfWeek as startOfCalendarWeek } from "date-fns";
/***/

/* Sections */
import Loading from "../../components/loading/Loading";
import PlanningCalendar from "./sections/planning-calendar/PlanningCalendar";
import PlanningDrawer from "./sections/planning-drawer/PlanningDrawer";
/***/

/* Services */
import { createDateQuery, createSingleQuery, SearchOptions, searchWithFilters } from "../../services/api/search";
/***/

/* Styles */
import "./Planning.scss";
import UnscheludedDrawer from "./drawers/UnscheludedDrawer";
import UnasignedDrawer from "./drawers/UnasignedDrawer";
import TimeEditModal from "./modals/TimeEditModal";
import WorkorderEditModal from "./modals/WorkorderEditModal";
import TimeCreateModal from "./modals/TimeCreateModal";
import PlanningFilterForm, { getPlanningQueriesFromFilter } from "./filter/PlanningFilterForm";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
import { usePlanningFilters } from "../../hooks/filters/usePlanningFilters";
/***/

/**
* Planning: display the planning view 
*/
export function Planning() {
  const [timesState, setTimesState] = useState<EasyTech.Time[]>();
  const [unscheduledWorkorders, setUnscheduledWorkorders] = useState<EasyTech.Workorder[]>([]);
  const [unasignedWorkorders, setUnasignedWorkorders] = useState<EasyTech.Workorder[]>([]);
  const [drawerType, setDrawerType] = useState<string | null>(null);
  const [selectedCreateTime, setSelectedCreateTime] = useState<{start: Date, end: Date} | null>(null);
  const [selectedTime, setSelectedTime] = useState<EasyTech.Time | null>(null);
  const [selectedWorkorder, setSelectedWorkorder] = useState<EasyTech.Workorder | null>(null);
  const [actualDate, setActualDateState] = useState<Date>(startOfCalendarWeek(new Date(), { weekStartsOn: 1 }));
  const [currentView, setCurrentView] = useState<"month" | "week" | "work_week" | "day">("week");
  const [loadingState, setLoadingState] = useState<boolean>(false);

  // Hook personnalisé pour gérer les opérations sur les times
  const useTimesOperations = () => {
    const updateTime = useCallback((time: EasyTech.Time) => {
      setTimesState(prev => {
        if (!prev) return prev;
        const index = prev.findIndex(el => el.id === time.id);
        if (index === -1) return prev;
        
        const updated = [...prev];
        updated[index] = time;
        return updated;
      });
      setSelectedTime(time);
    }, []);

    const createTime = useCallback((time: EasyTech.Time) => {
      setTimesState(prev => prev ? [...prev, time] : [time]);
      setSelectedCreateTime(null);
    }, []);

    const deleteTime = useCallback((time: EasyTech.Time) => {
      setTimesState(prev => {
        if (!prev) return prev;
        return prev.filter(t => t.id !== time.id);
      });
    }, []);

    return { updateTime, createTime, deleteTime };
  };

  const { updateTime, createTime, deleteTime: handleTimeDelete } = useTimesOperations();

  const onEventChange = (el: EasyTech.Time) => {
    if (!selectedTime || (selectedTime && el.id !== selectedTime.id)) {
      setSelectedWorkorder(null);
      setSelectedCreateTime(null);
      setSelectedTime(el);
    } else {
      setSelectedTime(null);
    }
  };

  const onWorkorderChange = (el: EasyTech.Workorder) => {
    if (!selectedWorkorder || (selectedWorkorder && el.id !== selectedWorkorder.id)) {
      setSelectedTime(null);
      setSelectedCreateTime(null);
      setSelectedWorkorder(el);
    } else {
      setSelectedWorkorder(null);
    }
  };

  const onTimeClick = (info: {start: Date, end: Date}) => {
    setSelectedTime(null);
    setSelectedWorkorder(null);
    setSelectedCreateTime(info);
  };



  // Utilisation du hook de filtres
  const { 
    filters, 
    handleFilterSubmit, 
    clearFilter, 
    clearAllFilters,
    isInitialized
  } = usePlanningFilters(() => null);

  const loadWorkordersWithFilters = useCallback(() => {
    if (!filters) return;

    setLoadingState(true);

    // Interventions sans technicien, uniquement au statut New, les plus anciennes en premier
    const unasignedQueries = [];
    const planningQueries = getPlanningQueriesFromFilter(filters, false);
    unasignedQueries.push(...planningQueries);
    unasignedQueries.push(createSingleQuery("technician", "", "IS_NULL"));
    unasignedQueries.push(createSingleQuery("status", "CREATED", "EQUAL"));

    const unasignedSearchOptions: SearchOptions = {
      paginate: {
        pageSize: 50,
        pageNumber: 0,
      },
      order: {
        by: "created",
        direction: "ASC",
      },
      queries: unasignedQueries
    };

    searchWithFilters<EasyTech.Workorder>(
      "workorders",
      unasignedSearchOptions,
      true,
      true
    ).then(ret => {
      // search-all ignore le tri demandé : on classe ici par date de création, plus anciennes en haut
      const workorders = [...(ret.data || [])].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
      );
      setUnasignedWorkorders(workorders);
    }).catch(error => {
      console.error("Erreur lors du chargement des workorders non assignés:", error);
      setUnasignedWorkorders([]);
    });

    // Interventions sans créneau planifié, uniquement au statut New, les plus anciennes en premier
    const unscheduledQueries = [];
    unscheduledQueries.push(...planningQueries);
    unscheduledQueries.push(createSingleQuery("schedule", "", "IS_NULL"));
    unscheduledQueries.push(createSingleQuery("status", "CREATED", "EQUAL"));

    const unscheduledSearchOptions: SearchOptions = {
      paginate: {
        pageSize: 50,
        pageNumber: 0,
      },
      order: {
        by: "created",
        direction: "ASC",
      },
      queries: unscheduledQueries
    };

    searchWithFilters<EasyTech.Workorder>(
      "workorders",
      unscheduledSearchOptions,
      true,
      true
    ).then(ret => {
      // search-all ignore le tri demandé : on classe ici par date de création, plus anciennes en haut
      const workorders = [...(ret.data || [])].sort(
        (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
      );
      setUnscheduledWorkorders(workorders);
    }).catch(error => {
      console.error("Erreur lors du chargement des workorders non schedulés:", error);
      setUnscheduledWorkorders([]);
    });
  }, [filters]);

  const loadTimesWithFilters = useCallback(() => {
    if (!actualDate) return;

    setLoadingState(true);

    let startDate: Date;
    let endDate: Date;

    switch (currentView) {
    case "day":
      startDate = startOfDay(actualDate);
      endDate = endOfDay(actualDate);
      break;
    case "week":
    case "work_week":
      startDate = startOfCalendarWeek(actualDate, { weekStartsOn: 1 });
      endDate = endOfWeek(actualDate, { weekStartsOn: 1 });
      break;
    case "month":
    default:
      startDate = startOfCalendarWeek(startOfCalendarMonth(actualDate), { weekStartsOn: 1 });
      endDate = endOfWeek(endOfMonth(actualDate), { weekStartsOn: 1 });
      break;
    }

    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();

    console.log("📅 Période de chargement:", {
      start: startISO,
      end: endISO,
      actualDate: actualDate.toISOString(),
      filters
    });
    
    const planningQueries = getPlanningQueriesFromFilter(filters || {}, false);

    const buildOverlapQueries = (startField: string, endField: string) => [
      createDateQuery(startField, endDate, "LESS_THAN"),
      createDateQuery(endField, startDate, "GREATER_THAN"),
      ...planningQueries
    ];

    const searchOptions = {
      order: {
        by: "date_from",
        direction: "ASC" as const
      },
      paginate: {
        pageNumber: 0,
        pageSize: 200
      }
    };

    const scheduledSearchOptions: SearchOptions = {
      ...searchOptions,
      queries: buildOverlapQueries("date_from", "date_to")
    };

    const realTimeSearchOptions: SearchOptions = {
      ...searchOptions,
      queries: buildOverlapQueries("workOrder.time.start", "workOrder.time.end")
    };

    console.log("Requête planning:", {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      scheduledSearchOptions,
      realTimeSearchOptions
    });

    // Chevauchement sur dates planifiées OU dates réelles (aligné avec l'affichage du calendrier)
    Promise.all([
      searchWithFilters<EasyTech.Time>("technician_times", scheduledSearchOptions, true, true),
      searchWithFilters<EasyTech.Time>("technician_times", realTimeSearchOptions, true, true)
    ])
      .then(([scheduledResult, realTimeResult]) => {
        const mergedById = new Map<string, EasyTech.Time>();
        (scheduledResult.data || []).forEach((time) => mergedById.set(time.id, time));
        (realTimeResult.data || []).forEach((time) => mergedById.set(time.id, time));

        const convertedTimes = Array.from(mergedById.values())
          .sort((a, b) => new Date(a.date_from).getTime() - new Date(b.date_from).getTime())
          .map((time: EasyTech.Time) => ({
            ...time,
            date_from: new Date(time.date_from).toISOString(),
            date_to: new Date(time.date_to).toISOString()
          }));

        console.log("Times convertis pour le calendrier:", convertedTimes);
        setTimesState(convertedTimes);
        setLoadingState(false);
      }).catch((error: any) => {
        console.error("Erreur lors du chargement du planning:", error);
        setLoadingState(false);
      });
  }, [actualDate, currentView, filters]);

  // Fonction pour mettre à jour un workorder après modification
  const updateWorkorder = useCallback((wo: EasyTech.Workorder) => {
    // Utiliser la forme fonctionnelle pour éviter les dépendances aux listes
    setUnscheduledWorkorders(prev => {
      const index = prev.findIndex(el => el.id === wo.id);
      if (index === -1) return prev;

      if (wo.status !== "CREATED" || (drawerType === "unscheluded" && wo.schedule)) {
        return prev.filter(w => w.id !== wo.id);
      }
      const updated = [...prev];
      updated[index] = wo;
      return updated;
    });
    
    setUnasignedWorkorders(prev => {
      const index = prev.findIndex(el => el.id === wo.id);
      if (index === -1) return prev;

      if (wo.status !== "CREATED" || (drawerType === "unasigned" && wo.technician)) {
        return prev.filter(w => w.id !== wo.id);
      }
      const updated = [...prev];
      updated[index] = wo;
      return updated;
    });
    
    setSelectedWorkorder(wo);
    if (wo.schedule && wo.technician) {
      loadTimesWithFilters();
    }
  }, [drawerType, loadTimesWithFilters]);

  /**
  * useEffect: Charger les workorders quand les filtres sont initialisés ou changent
  */
  useEffect(() => {
    if (!isInitialized) return;
    loadWorkordersWithFilters();
  }, [loadWorkordersWithFilters, isInitialized]);
  /***/

  /**
  * useEffect: Charger les times quand les filtres ou la date changent
  */
  useEffect(() => {
    if (!isInitialized || !filters) return;

    console.log("🚀 useEffect loadTimesWithFilters déclenché", { actualDate, filters });
    loadTimesWithFilters();
  }, [loadTimesWithFilters, isInitialized, actualDate, filters]);
  /***/

  const filterDrawerComponent = (
    <FilterDrawer<EasyTech.PlanningFilterType>
      FormComponent={PlanningFilterForm}
      onSubmit={handleFilterSubmit}
      entityName="Planning"
      data={filters}
      size="md"
      noFlex
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={filters}
      onClearFilter={clearFilter}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
    />
  );

  return (
    <div className={`planning ${drawerType && "open"}`}>
      <div className="planning-wrapper views-padded">
        <div className="planning-content">
          {timesState ?
            <PlanningCalendar times={timesState} loading={loadingState} 
              onEventClick={onEventChange} 
              onTimeClick={onTimeClick} 
              unscheduledWorkordersLength={unscheduledWorkorders.length}
              unasignedWorkordersLength={unasignedWorkorders.length}
              drawerType={drawerType}
              setDrawerType={(type) => setDrawerType(type)}
              filterDrawerComponent={filterDrawerComponent}
              filterBadgesComponent={filterBadgesComponent}
              onPlanningNavigate={(newDate, view) => {
                if (currentView !== view) {
                  setCurrentView(view as "month" | "week" | "work_week" | "day");
                }
                if (actualDate.getTime() !== newDate.getTime())
                  setActualDateState(newDate);
              }} />
          : <Loading/>}
        </div>
      </div>

      <PlanningDrawer open={drawerType ? true: false} onClose={() => {
        setDrawerType(null);
        setSelectedTime(null);
      }}>
        {drawerType === "unscheluded" && <UnscheludedDrawer onTimeClick={onWorkorderChange} workorders={unscheduledWorkorders || []}/>}
        {drawerType === "unasigned" && <UnasignedDrawer onTimeClick={onWorkorderChange} workorders={unasignedWorkorders || []}/>}
      </PlanningDrawer>

      {selectedCreateTime &&
        <TimeCreateModal open={selectedCreateTime !== null} onTimeCreate={createTime} times={selectedCreateTime} onClose={() => setSelectedCreateTime(null)} /> 
      }

      {selectedTime &&
        <TimeEditModal open={selectedTime !== null} onUpdate={updateTime} onDelete={handleTimeDelete} time={selectedTime} onClose={() => setSelectedTime(null)} /> 
      }

      {selectedWorkorder &&
        <WorkorderEditModal open={selectedWorkorder !== null} onUpdate={updateWorkorder} 
          workorder={selectedWorkorder} onClose={() => setSelectedWorkorder(null)} 
          defaultEdit={drawerType === "unscheluded" ? "schedule" : "tech"} /> 
      }
    </div>
  );
}
/***/
